/* eslint-disable no-useless-escape */
import {
  ALPHANUMERIC_CNPJ_REGEX,
  isAlphanumericCnpjBase,
  stripTaxpayerId,
} from 'app/helpers/masks';

export const isValidCNPJFormat = (value: string): boolean => {
  if (!value || typeof value !== 'string') return false;

  const stripped = stripTaxpayerId(value);
  return ALPHANUMERIC_CNPJ_REGEX.test(stripped);
};

const hasRepeatedCnpjCharacters = (stripped: string): boolean =>
  new Set(stripped.split('')).size === 1;

const charVal = (character: string): number => character.charCodeAt(0) - 48;

const calculateCnpjCheckDigit = (chars: string[], length: number): number => {
  let factor = length - 7;
  let sum = 0;

  for (let index = length; index >= 1; index--) {
    sum += charVal(chars[length - index]) * factor;
    factor -= 1;
    if (factor < 2) factor = 9;
  }

  const remainder = sum % 11;
  const result = 11 - remainder;
  return result > 9 ? 0 : result;
};

const isValidNumericCnpjCheckDigits = (stripped: string): boolean => {
  const chars = stripped.split('');

  return (
    calculateCnpjCheckDigit(chars, 12) === charVal(chars[12]) &&
    calculateCnpjCheckDigit(chars, 13) === charVal(chars[13])
  );
};

export function isValidCNPJ(value: string) {
  if (!isValidCNPJFormat(value)) return false;

  const stripped = stripTaxpayerId(value);

  if (hasRepeatedCnpjCharacters(stripped)) return false;

  if (isAlphanumericCnpjBase(stripped)) return true;

  return isValidNumericCnpjCheckDigits(stripped);
}

export const isValidEmail = (email: string): boolean => {
  const emailRegex = new RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );
  return emailRegex.test(email);
};

export const isValidPhoneNumber = (phone: string): boolean => {
  return phone.replace(/[^\w\s]/gi, '').replace(/\s/g, '').length === 11;
};

export const isValidKey = (key: string): boolean => {
  const keyRegex = new RegExp(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/,
  );
  return keyRegex.test(key);
};

export function isJSON(str: string | null): boolean {
  try {
    if (typeof str === 'string') {
      JSON.parse(str);
      return true;
    }
  } catch (e) {
    return false;
  }
  return false;
}

export function isValidCPF(value: string) {
  if (typeof value !== 'string') {
    return false;
  }

  value = value.replace(/[^\d]+/g, '');

  if (
    value.length !== 11 ||
    !!value.match(/(\d)\1{10}/) ||
    value === '00000000000' ||
    value === '11111111111' ||
    value === '22222222222' ||
    value === '33333333333' ||
    value === '44444444444' ||
    value === '55555555555' ||
    value === '66666666666' ||
    value === '77777777777' ||
    value === '88888888888' ||
    value === '99999999999'
  ) {
    return false;
  }

  const values = value.split('').map((el) => +el);
  const rest = (count: number) =>
    ((values
      .slice(0, count - 12)
      .reduce((soma, el, index) => soma + el * (count - index), 0) *
      10) %
      11) %
    10;

  return rest(10) === values[9] && rest(11) === values[10];
}
